/**
 * Groups Table Section Component
 * Displays the groups table with actions and pagination
 * Extracted from VPEDashboard.tsx for better modularity
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Mail,
  Edit,
  Archive,
  Trash2,
  Copy
} from 'lucide-react';
import { Group } from '@/services/ea/vpeDashboardService';
import PaginationComponent from '@/components/common/PaginationComponent';

interface GroupsTableSectionProps {
  // Data
  paginatedGroups: Group[];
  isLoading: boolean;
  archiveButtonLoading: number | null;
  
  // Pagination
  currentPage: number;
  totalPages: number;
  rowsPerPage: number;
  startIndex: number;
  totalGroups: number;
  
  // Actions
  onEmailClick: (groupId: number) => void;
  onEditClick: (groupId: number) => void;
  onDuplicateClick: (groupId: number) => void;
  onArchiveClick: (groupId: number) => void;
  onDeleteClick: (groupId: number) => void;
  onPageChange: (page: number) => void;
  onRecordsPerPageChange: (recordsPerPage: number) => void;
}

const GroupsTableSection: React.FC<GroupsTableSectionProps> = ({
  paginatedGroups,
  isLoading,
  archiveButtonLoading,
  currentPage,
  totalPages,
  rowsPerPage,
  startIndex,
  totalGroups,
  onEmailClick,
  onEditClick,
  onDuplicateClick,
  onArchiveClick,
  onDeleteClick,
  onPageChange,
  onRecordsPerPageChange,
}) => {
  const endIndex = startIndex + rowsPerPage;
  if (isLoading) {
    return (
      <Card className="bg-white shadow-2xl border-0 rounded-2xl overflow-hidden" data-testid="ea-groups-table-loading">
        <CardContent className="p-0">
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (paginatedGroups.length === 0) {
    return (
      <Card className="bg-white shadow-2xl border-0 rounded-2xl overflow-hidden" data-testid="ea-groups-table-empty">
        <CardContent className="p-0">
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Search className="h-12 w-12 text-gray-300" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">No Records Found</h3>
            <p className="text-gray-500">Try adjusting your search criteria or filters.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white shadow-2xl border-0 rounded-2xl overflow-hidden" data-testid="ea-groups-table">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="ea-groups-table-content">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200" data-testid="ea-groups-table-header">
              <tr>
                <th className="text-left py-6 px-8 font-bold text-gray-700 text-sm uppercase tracking-wider" data-testid="ea-groups-table-header-group-id">Group ID</th>
                <th className="text-left py-6 px-8 font-bold text-gray-700 text-sm uppercase tracking-wider" data-testid="ea-groups-table-header-group-name">Group Name</th>
                <th className="text-left py-6 px-8 font-bold text-gray-700 text-sm uppercase tracking-wider" data-testid="ea-groups-table-header-type">Type</th>
                <th className="text-left py-6 px-8 font-bold text-gray-700 text-sm uppercase tracking-wider" data-testid="ea-groups-table-header-assigned-vpe">Assigned VPE</th>
                <th className="text-left py-6 px-8 font-bold text-gray-700 text-sm uppercase tracking-wider" data-testid="ea-groups-table-header-clinic-name">Clinic Name</th>
                <th className="text-left py-6 px-8 font-bold text-gray-700 text-sm uppercase tracking-wider" data-testid="ea-groups-table-header-actions">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="ea-groups-table-body">
              {paginatedGroups.map((group, index) => (
                <tr key={group.group_id} className={`border-b border-gray-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`} data-testid={`ea-groups-table-row-${group.group_id}`}>
                  <td className="py-6 px-8" data-testid={`ea-groups-table-cell-group-id-${group.group_id}`}>
                    <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200 font-bold rounded-lg px-3 py-1" data-testid={`ea-groups-badge-group-id-${group.group_id}`}>
                      {group.group_id}
                    </Badge>
                  </td>
                  <td className="py-6 px-8 font-bold text-gray-900 text-lg max-w-xs" data-testid={`ea-groups-table-cell-group-name-${group.group_id}`}>
                    <div 
                      className="break-words overflow-hidden"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: '1.4em',
                        maxHeight: '2.8em'
                      }}
                      data-testid={`ea-groups-name-text-${group.group_id}`}
                    >
                      {group.group_name}
                    </div>
                  </td>
                  <td className="py-6 px-8" data-testid={`ea-groups-table-cell-type-${group.group_id}`}>
                    <Badge variant="secondary" className="bg-purple-100 text-purple-800 border-purple-200 rounded-lg px-3 py-1 font-semibold" data-testid={`ea-groups-badge-type-${group.group_id}`}>
                      {group.group_type}
                    </Badge>
                  </td>
                  <td className="py-6 px-8 text-gray-700 font-semibold" data-testid={`ea-groups-table-cell-assigned-vpe-${group.group_id}`}>
                    {group.es_name || 'Not assigned'}
                  </td>
                  <td className="py-6 px-8 text-gray-700 font-semibold" data-testid={`ea-groups-table-cell-clinic-name-${group.group_id}`}>{group.clinic_name}</td>
                  <td className="py-6 px-8" data-testid={`ea-groups-table-cell-actions-${group.group_id}`}>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 border-blue-200 bg-blue-50 hover:bg-blue-100 hover:border-blue-300 transition-all duration-200 rounded-lg shadow-sm"
                        onClick={() => onEmailClick(group.group_id)}
                        data-testid={`ea-groups-email-button-${group.group_id}`}
                      >
                        <Mail className="h-4 w-4 text-blue-600" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 border-green-200 bg-green-50 hover:bg-green-100 hover:border-green-300 transition-all duration-200 rounded-lg shadow-sm"
                        onClick={() => onEditClick(group.group_id)}
                        data-testid={`ea-groups-edit-button-${group.group_id}`}
                      >
                        <Edit className="h-4 w-4 text-green-600" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 border-purple-200 bg-purple-50 hover:bg-purple-100 hover:border-purple-300 transition-all duration-200 rounded-lg shadow-sm"
                        onClick={() => onDuplicateClick(group.group_id)}
                        data-testid={`ea-groups-duplicate-button-${group.group_id}`}
                      >
                        <Copy className="h-4 w-4 text-purple-600" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 border-orange-200 bg-orange-50 hover:bg-orange-100 hover:border-orange-300 transition-all duration-200 rounded-lg shadow-sm"
                        onClick={() => onArchiveClick(group.group_id)}
                        disabled={archiveButtonLoading === group.group_id}
                        data-testid={`ea-groups-archive-button-${group.group_id}`}
                      >
                        {archiveButtonLoading === group.group_id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
                        ) : (
                          <Archive className="h-4 w-4 text-orange-600" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 border-red-200 bg-red-50 hover:bg-red-100 hover:border-red-300 transition-all duration-200 rounded-lg shadow-sm"
                        onClick={() => onDeleteClick(group.group_id)}
                        data-testid={`ea-groups-delete-button-${group.group_id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Common Pagination Component */}
        <div className="border-t border-gray-100 bg-gradient-to-r from-gray-50 to-gray-100" data-testid="ea-groups-table-pagination-container">
          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            recordsPerPage={rowsPerPage}
            totalRecords={totalGroups}
            startIndex={startIndex}
            endIndex={endIndex}
            onPageChange={onPageChange}
            onRecordsPerPageChange={onRecordsPerPageChange}
            className="py-6 px-8"
            data-testid="ea-groups-table-pagination"
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default GroupsTableSection;